import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TenantsApiKey as ApiKey, TenantsApiKeyCreate as ApiKeyCreate, TenantsApiKeyScope as ApiKeyScope } from '../generated/api/types.gen.ts';
import { apiKeysCreate, apiKeysList, apiKeysRevoke } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';

export type { ApiKey, ApiKeyCreate, ApiKeyScope };

/**
 * Kontraktens tillatelser, i kontraktens rekkefølge, med et menneskelig ord ved siden av.
 * `gir` er det som leses på skjermen; ledningsnavnet står lite under (DD-50, DD-60).
 */
export const SCOPES: ReadonlyArray<{ scope: ApiKeyScope; gir: string }> = [
  { scope: 'admin', gir: 'alt serveren deres kan gjøre' },
  { scope: 'segment:read', gir: 'lese programmets segment' },
  // K11: sletting per pseudonym innen 24 timer, med kvittering.
  { scope: 'subjects:delete', gir: 'be om sletting per pseudonym' },
  { scope: 'webhooks', gir: 'lese leveringsloggen' },
  { scope: 'programs:confirm', gir: 'bekrefte feature-flagg og utmelding' },
];

/** Nøklene eies her og bare her (`react-ts-integration`). */
export const apiNoekkelNoekkel = {
  alle: ['api-keys'] as const,
  liste: ['api-keys', 'liste'] as const,
};

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** ApiKeys uten klartekst (DD-20). Kun eier — andre roller får 403, og hooken skal ikke kalles for dem. */
export function useApiNoekler(aktiv: boolean) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: apiNoekkelNoekkel.liste,
    enabled: aktiv,
    queryFn: async (): Promise<ApiKey[]> => {
      const res = await apiKeysList({ client: klient });
      // Et svar som ikke er en liste er en feil, ikke en tom liste — seksjonen viser feiltilstanden.
      if (!res.response?.ok || !Array.isArray(res.data)) kast(res);
      return res.data as ApiKey[];
    },
  });
}

function useKall<TArg, TRes>(fn: (arg: TArg) => Promise<{ response?: Response; data?: unknown; error?: unknown }>, etter: () => void) {
  return useMutation({
    mutationFn: async (arg: TArg): Promise<TRes> => {
      const res = await fn(arg);
      if (!res.response?.ok) kast(res);
      return res.data as TRes;
    },
    onSuccess: etter,
  });
}

/** Opprett: svaret bærer klarteksten — den ene gangen (DD-20). Kun eier. */
export function useOpprettApiNoekkel() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<ApiKeyCreate, ApiKey>(
    (body) => apiKeysCreate({ client: klient, body }),
    () => void queryClient.invalidateQueries({ queryKey: apiNoekkelNoekkel.alle }),
  );
}

/** Kall tilbake: 401 fra neste kall. Idempotent. Kun eier. */
export function useKallTilbakeApiNoekkel() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useKall<string, void>(
    (id) => apiKeysRevoke({ client: klient, path: { id } }),
    () => void queryClient.invalidateQueries({ queryKey: apiNoekkelNoekkel.alle }),
  );
}
